import React from 'react';
import { Box } from '@mui/material';
import { formatSiteDateTime } from '../../domain/siteTime/siteTime';
import type { ConditionLifecycleSummary } from '../../domain/continuousSafety/siteConditionState';
import {
  buildConditionLifecyclePath,
  buildLifecycleAfterReportPath,
  buildLifecycleBeforeReportPath,
  buildLifecycleControlPath,
  buildLifecycleEvidencePath,
  buildLifecycleImmediateIncidentPath,
  buildLifecyclePatternIncidentPath,
  buildLifecyclePatternMembershipPath,
  buildLifecycleScanVerifyPath,
  buildLifecycleVisualPath,
  isApiLifecycleHref,
} from '../../domain/continuousSafety/conditionLifecycleNavigation';
import {
  immediateVsPatternCopy,
  presentPatternAlertReason,
  presentPostControlPattern,
  recurrenceIsNotAlarmCopy,
  resolveCanonicalControl,
  scanIsNotPatternAlertCopy,
} from '../../domain/continuousSafety/conditionLifecyclePresentation';

export type ConditionLifecycleSpineDensity = 'om' | 'scan' | 'page';

export type ConditionLifecycleSpineProps = {
  lifecycle: ConditionLifecycleSummary;
  siteId: string;
  conditionId: string;
  areaId?: string | null;
  zoneId?: string | null;
  cameraId?: string | null;
  orgId?: string | null;
  incidentType?: string | null;
  conditionFamily?: string | null;
  safetyCaseId?: string | null;
  controlRecordPath?: string | null;
  legacyControlId?: string | null;
  legacyControlTitle?: string | null;
  recurrenceLabel?: string | null;
  timeZone?: string | null;
  density?: ConditionLifecycleSpineDensity;
  onNavigate?: (path: string) => void;
  testId?: string;
};

function LifecycleLink({
  href,
  children,
  onNavigate,
  testId,
}: {
  href: string | null;
  children: React.ReactNode;
  onNavigate?: (path: string) => void;
  testId?: string;
}) {
  if (!href || isApiLifecycleHref(href)) {
    return <Box component="span" data-testid={testId}>{children}</Box>;
  }
  return (
    <Box
      component="a"
      href={href}
      data-testid={testId}
      onClick={(event: React.MouseEvent<HTMLAnchorElement>) => {
        event.preventDefault();
        if (onNavigate) onNavigate(href);
      }}
      sx={{ color: '#1d4ed8', textDecoration: 'none', fontWeight: 650 }}
    >
      {children}
    </Box>
  );
}

export function ConditionLifecycleSpine({
  lifecycle,
  siteId,
  conditionId,
  areaId,
  zoneId,
  cameraId,
  orgId,
  incidentType,
  conditionFamily,
  safetyCaseId,
  controlRecordPath,
  legacyControlId,
  legacyControlTitle,
  recurrenceLabel,
  timeZone,
  density = 'om',
  onNavigate,
  testId,
}: ConditionLifecycleSpineProps) {
  const routeArgs = { siteId, conditionId, areaId, zoneId, cameraId, orgId };
  const immediate = lifecycle.latestImmediateIncident;
  const pattern = lifecycle.latestTemporalPatternIncident;
  const scan = lifecycle.latestScan;
  const reason = presentPatternAlertReason(pattern);
  const control = resolveCanonicalControl({
    lifecycle,
    legacyControlId,
    legacyControlTitle,
  });
  const postControl = presentPostControlPattern({
    sinceStartedAt: lifecycle.sinceStartedAt,
    latestPattern: pattern,
    scan,
    backend: lifecycle.postControlPattern,
  });
  const sinceLabel = lifecycle.sinceStartedAt
    ? (formatSiteDateTime(lifecycle.sinceStartedAt, timeZone) || lifecycle.sinceStartedAt)
    : 'implementation time unavailable';
  const immediateTime = immediate?.occurredAt
    ? (formatSiteDateTime(immediate.occurredAt, timeZone) || immediate.occurredAt)
    : null;
  const patternTime = pattern?.occurredAt
    ? (formatSiteDateTime(pattern.occurredAt, timeZone) || pattern.occurredAt)
    : null;
  const immediateHref = immediate
    ? buildLifecycleImmediateIncidentPath({
      ...routeArgs,
      incidentId: immediate.incidentId,
      incidentType,
      conditionFamily,
    })
    : null;
  const patternHref = pattern
    ? buildLifecyclePatternIncidentPath({
      ...routeArgs,
      incidentId: pattern.incidentId,
      incidentType,
      conditionFamily,
    })
    : null;
  const membershipHref = buildLifecyclePatternMembershipPath(routeArgs);
  const visualHref = buildLifecycleVisualPath(routeArgs);
  const controlHref = control.authority === 'none'
    ? null
    : buildLifecycleControlPath({
      ...routeArgs,
      controlRecordPath: control.authority === 'legacy' ? controlRecordPath : null,
      hasEpic402Control: control.authority === 'epic402',
    });
  const afterReportId = scan?.afterReportId || null;
  const beforeReportId = scan?.beforeReportId || null;
  const scanHref = afterReportId
    ? buildLifecycleScanVerifyPath({
      ...routeArgs,
      reportId: afterReportId,
      safetyCaseId,
    })
    : buildConditionLifecyclePath({ ...routeArgs, focus: 'scan' });
  const beforeHref = beforeReportId
    ? buildLifecycleBeforeReportPath({ ...routeArgs, reportId: beforeReportId, safetyCaseId })
    : null;
  const afterHref = afterReportId
    ? buildLifecycleAfterReportPath({ ...routeArgs, reportId: afterReportId, safetyCaseId })
    : null;
  const evidenceHref = buildLifecycleEvidencePath({
    ...routeArgs,
    reportId: afterReportId,
    incidentId: scan?.motivatingIncidents[0]?.incidentId || pattern?.incidentId || immediate?.incidentId,
    incidentType,
    conditionFamily,
    safetyCaseId,
  });
  const compact = density === 'om';
  const fontSize = compact ? 9.5 : 13;
  const rowSx = { fontSize, color: '#4a5768', mt: '2px', lineHeight: 1.35 };

  return (
    <Box data-testid={testId} data-lifecycle-density={density}>
      {density !== 'om' ? (
        <Box sx={{ fontSize: compact ? 8 : 11, fontWeight: 750, letterSpacing: '.65px', textTransform: 'uppercase', color: '#7d8798' }}>
          Alarm → Control → Since → Scan
        </Box>
      ) : (
        <Box sx={{ fontSize: 8, fontWeight: 750, letterSpacing: '.65px', textTransform: 'uppercase', color: '#7d8798' }}>
          Recurrence vs Alert vs Control vs Since vs Verification
        </Box>
      )}
      <Box sx={rowSx} data-testid="lifecycle-recurrence">
        Recurrence · {recurrenceLabel || 'Overview occurrence rate'} · {recurrenceIsNotAlarmCopy()}
      </Box>
      {immediate ? (
        <Box sx={rowSx} data-testid="lifecycle-immediate-alarm">
          <LifecycleLink href={immediateHref} onNavigate={onNavigate} testId="lifecycle-immediate-link">
            Alert · Immediate · {immediate.severity || 'severity unavailable'}
            {immediateTime ? ` · ${immediateTime}` : ''}
          </LifecycleLink>
          {!compact ? ` · ${immediateVsPatternCopy('IMMEDIATE')}` : null}
        </Box>
      ) : (
        <Box sx={rowSx} data-testid="lifecycle-immediate-alarm">Alert · Immediate · none linked</Box>
      )}
      {pattern ? (
        <Box sx={rowSx} data-testid="lifecycle-pattern-alarm">
          <LifecycleLink href={patternHref} onNavigate={onNavigate} testId="lifecycle-pattern-link">
            Alert · Pattern · {reason?.summary || pattern.patternRule || `${pattern.observedCount || '—'} observations`}
            {patternTime ? ` · ${patternTime}` : ''}
          </LifecycleLink>
          <Box component="span"> · </Box>
          <LifecycleLink href={membershipHref} onNavigate={onNavigate} testId="lifecycle-pattern-membership-link">
            Pattern membership
          </LifecycleLink>
        </Box>
      ) : (
        <Box sx={rowSx} data-testid="lifecycle-pattern-alarm">Alert · Pattern · none linked</Box>
      )}
      <Box sx={rowSx} data-testid="lifecycle-control">
        {controlHref ? (
          <LifecycleLink href={controlHref} onNavigate={onNavigate} testId="lifecycle-control-link">
            Control · {control.label}
            {control.authority === 'epic402' ? ` · Since ${sinceLabel}` : ''}
          </LifecycleLink>
        ) : (
          <>Control · {control.label}</>
        )}
        {control.legacyNote ? ` · ${control.legacyNote}` : null}
      </Box>
      <Box sx={rowSx} data-testid="lifecycle-since">
        Since · {control.authority === 'epic402' ? sinceLabel : 'No linked Control implementation time'}
      </Box>
      {scan ? (
        <Box sx={rowSx} data-testid="lifecycle-scan">
          <LifecycleLink href={scanHref} onNavigate={onNavigate} testId="lifecycle-scan-link">
            Verification · Scan · {scan.resultDirection || 'result pending'}
            {' · '}{scan.controlRevisionId ? 'Control-linked' : 'No Control link'}
            {' · Coverage '}{scan.coverageState || 'unavailable'}
            {' · '}{scan.finality || 'finality unavailable'}
          </LifecycleLink>
          {density !== 'om' ? ` · ${scanIsNotPatternAlertCopy()}` : null}
        </Box>
      ) : (
        <Box sx={rowSx} data-testid="lifecycle-scan">Verification · Scan · none linked</Box>
      )}
      <Box sx={rowSx} data-testid="lifecycle-before-after">
        Before{' '}
        {beforeHref ? (
          <LifecycleLink href={beforeHref} onNavigate={onNavigate} testId="lifecycle-before-link">
            {beforeReportId}
          </LifecycleLink>
        ) : 'not linked'}
        {' · '}After{' '}
        {afterHref ? (
          <LifecycleLink href={afterHref} onNavigate={onNavigate} testId="lifecycle-after-link">
            {afterReportId}
          </LifecycleLink>
        ) : 'not linked'}
      </Box>
      <Box sx={rowSx} data-testid="lifecycle-post-control-pattern">
        {postControl.summary}
      </Box>
      {density === 'om' ? (
        <Box sx={rowSx} data-testid="lifecycle-visual-entry">
          <LifecycleLink href={visualHref} onNavigate={onNavigate} testId="lifecycle-visual-link">
            Open visual lifecycle
          </LifecycleLink>
        </Box>
      ) : density !== 'scan' ? (
        <Box sx={rowSx} data-testid="lifecycle-evidence">
          <LifecycleLink href={evidenceHref} onNavigate={onNavigate} testId="lifecycle-evidence-link">
            Evidence
          </LifecycleLink>
          {lifecycle.populationExplanation ? ` · ${lifecycle.populationExplanation}` : null}
        </Box>
      ) : null}
    </Box>
  );
}
